import { getLanguageByLocale } from '@diagentech/exprworks-rule-engine';
import { useTranslation } from '@diagentech/exprworks-ui-components';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/App/hooks';
import { LocaleType } from '@/App/types';
import { selectLanguageSwitchEnabled } from '@/features/config/selectLanguageSwitchEnabled';
import { changeLanguageThunk } from '@/features/i18n/i18nSlice';
import { selectLanguage } from '@/features/i18n/selectLanguage';
import { selectLanguages } from '@/features/i18n/selectLanguages';
import { selectLocale } from '@/features/i18n/selectLocale';
import { useAuth } from '@/hooks/useAuth';
import { useLocale } from '@/hooks/useLocale';

const LANGUAGE_BUTTON_ID = 'language-button';
const LANGUAGE_MENU_ID = 'language-menu';

interface LanguageSwitcherProps {
  buttonId?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ buttonId = LANGUAGE_BUTTON_ID }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const dispatch = useAppDispatch();
  const languages = useAppSelector(selectLanguages);
  const locale = useAppSelector(selectLocale);
  const language = useAppSelector(selectLanguage);
  const languageSwitchEnabled = useAppSelector(selectLanguageSwitchEnabled);
  const { userAuthenticated } = useAuth();
  const { update } = useLocale();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLocale = useCallback(
    (value: LocaleType) => {
      handleClose();
      if (value !== locale) {
        if (userAuthenticated) {
          const newLanguage = getLanguageByLocale(languages, value);
          if (newLanguage) {
            update(value);
            const { locale: newLocale, ...newLanguageRest } = newLanguage;
            dispatch(changeLanguageThunk({ ...newLanguageRest, locale: newLocale as LocaleType }));
          }
        } else {
          update(value);
        }
      }
    },
    [dispatch, languages, locale, userAuthenticated, update],
  );

  const changeLanguageText = useTranslation('SH.BTN.CHANGELANGUAGE');

  if (!languageSwitchEnabled) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title={changeLanguageText}>
          <IconButton
            id={buttonId}
            onClick={handleClick}
            size="small"
            aria-label={changeLanguageText}
            aria-controls={open ? LANGUAGE_MENU_ID : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            color="inherit"
          >
            <Stack direction={'row'} spacing={0.5} sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
              <LanguageIcon />
              {language && <Typography variant={'body2'}>{language.name}</Typography>}
            </Stack>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        id={LANGUAGE_MENU_ID}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
      >
        {languages?.map((language) => (
          <MenuItem onClick={() => changeLocale(language.locale)} key={language.id}>
            <Stack direction={'row'} spacing={1} sx={{ display: 'flex' }}>
              <Typography variant={'body2'}>{language.name}</Typography>
              {language.locale === locale && <CheckIcon color={'primary'} />}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
